import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Route } from "lucide-react";

export const StepRoutes = ({ state, patch, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const archetypes = (state.config?.archetypes || []).filter((a) => a.active !== false);
  const confirmed = (state.capabilities || []).filter((c) => c.user_status === "confirmed");

  const finish = (data, cached) => {
    patch({ hypotheses: data.hypotheses, progress: data.progress });
    toast.success(cached ? "Your saved hypotheses are ready" : `${data.hypotheses.length} hypotheses worth testing`);
    onNext();
  };

  const poll = async (jobId) => {
    for (let i = 0; i < 100; i += 1) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/accelerator/hypotheses/job/${jobId}`);
      if (data.status === "done") return finish(data, false);
      if (data.status === "error") throw new Error(data.message || "Could not build hypotheses");
      setNote(`Building your income routes… ${data.done}/${data.total}`);
    }
    throw new Error("This is taking longer than usual. Please retry.");
  };

  const generate = async (refresh = false) => {
    setLoading(true);
    setNote("Building your income routes…");
    try {
      const { data } = await api.post(`/accelerator/hypotheses/generate${refresh ? "?refresh=true" : ""}`);
      if (data.status === "done") finish(data, data.cached);
      else await poll(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not build hypotheses");
    } finally {
      setLoading(false);
      setNote("");
    }
  };

  return (
    <section className="space-y-4" data-testid="accelerator-step-routes-panel">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Step 3 — Possible income routes</h2>
        <p className="text-sm text-slate-500 mt-1.5">These are categories, not recommendations. Your confirmed capabilities are matched against them next — you still choose the direction.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {archetypes.map((a) => {
          const matches = confirmed.filter((c) => (c.candidate_archetypes || []).includes(a.id));
          return (
            <article key={a.id} className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4 sm:p-5" data-testid={`archetype-card-${a.id}`}>
              <div className="flex items-start gap-2">
                <Route className="w-4 h-4 text-sky-600 shrink-0 mt-1" />
                <h3 className="font-bold text-slate-900 break-words">{a.name}</h3>
              </div>
              <p className="mt-2 text-sm text-slate-500">{a.description}</p>
              {a.suits && <p className="mt-2 text-xs text-slate-500">May suit: {a.suits}</p>}
              {!!a.examples?.length && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {a.examples.map((e) => <span key={e} className="rounded-full border border-slate-300 px-2.5 py-1 text-[11px] text-slate-500">{e}</span>)}
                </div>
              )}
              <p className="mt-3 text-xs" data-testid={`archetype-matches-${a.id}`}>
                {matches.length ? <span className="text-emerald-700">Your confirmed capabilities that may fit: {matches.map((m) => m.name).join(", ")}</span>
                  : <span className="text-slate-500">No confirmed capability points here yet.</span>}
              </p>
            </article>
          );
        })}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500" data-testid="accelerator-routes-note">
          {note || `We'll turn your ${confirmed.length} confirmed capabilit${confirmed.length === 1 ? "y" : "ies"} into specific, testable hypotheses.`}
        </p>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button onClick={() => generate(false)} disabled={loading || !confirmed.length} className="h-11 bg-sky-600 hover:bg-sky-500 text-white" data-testid="accelerator-generate-hypotheses">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</> : "Generate hypotheses"}
          </Button>
          {!!state.hypotheses?.length && <Button onClick={() => generate(true)} disabled={loading} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="accelerator-regenerate-hypotheses">Re-run</Button>}
        </div>
      </div>
    </section>
  );
};

export default StepRoutes;
